/**
 * @component ry-relative-time
 * @category  display
 * @summary   Outputs a localized time phrase relative to the current date (e.g. "5 minutes ago", "in 2 hours"). Pure display — wraps `Intl.RelativeTimeFormat`, optionally re-renders on a 60s tick.
 *
 * Ported from Shoelace's `<sl-relative-time>` (MIT) —
 * https://github.com/shoelace-style/shoelace/blob/next/src/components/relative-time/relative-time.component.ts
 *
 * Picks the best unit (minute → hour → day → week → month → year) for the
 * absolute diff between `date` and "now", then formats the value using the
 * platform's `Intl.RelativeTimeFormat`. Renders the result inside a
 * `<time datetime="ISO">` element so assistive tech and crawlers get the
 * absolute timestamp.
 *
 * @attribute date     string | Date    The reference date. Accepts an ISO-8601 string, anything `new Date()` understands, or a `Date` instance. Strongly prefer ISO-8601 so timezones are unambiguous. Required — without it the element renders empty.
 * @attribute format   'long' | 'short' | 'narrow'   default 'long'. Maps to `Intl.RelativeTimeFormat`'s `style` option. `long` = "5 minutes ago", `short` = "5 min. ago", `narrow` = "5m ago" (locale-dependent).
 * @attribute numeric  'always' | 'auto'             default 'auto'. `auto` allows phrases like "yesterday" / "tomorrow"; `always` forces the numeric form ("1 day ago" / "in 1 day").
 * @attribute sync     boolean                       When present, the element re-renders every 60s while connected so the displayed value stays fresh. Cleaned up automatically on disconnect.
 * @attribute lang     string                        BCP 47 language tag. Forwarded to `Intl.RelativeTimeFormat`. If omitted, the document language (or the platform default) is used.
 *
 * @content  None — the rendered `<time>` element is generated and the host's innerHTML is replaced at setup.
 *
 * @dom
 *   ry-relative-time[date][format?][numeric?][sync?][lang?]
 *     > time[datetime="<ISO>"]    [data-ry-target="time"]
 *         > {"5 minutes ago" | "in 2 hours" | ...}
 *
 * @gotcha  An invalid `date` (e.g. `date="not-a-date"`) renders empty rather than throwing. Watch the DOM, not the console, when debugging.
 *
 * @gotcha  `sync` ticks every 60 seconds on a fixed interval — it does NOT align to wall-clock minute boundaries. Two `<ry-relative-time sync>` elements mounted seconds apart will update on different schedules. For dashboard parity, mount them together.
 *
 * @gotcha  Removing the `sync` attribute after mount does NOT stop the interval. The interval is wired in `setup()` and torn down in `teardown()`. Recreate the element if you need to switch sync off live.
 *
 * @example past
 * ```html
 * <ry-relative-time date="2026-05-30T11:55:00Z"></ry-relative-time>
 * <!-- → "5 minutes ago" (if now is 12:00 UTC) -->
 * ```
 *
 * @example future
 * ```html
 * <ry-relative-time date="2026-05-30T14:00:00Z"></ry-relative-time>
 * <!-- → "in 2 hours" -->
 * ```
 *
 * @example short
 * ```html
 * <ry-relative-time date="2026-05-29T12:00:00Z" format="short"></ry-relative-time>
 * <!-- → "1 day ago" → "1 day ago" (en-US short style) -->
 * ```
 *
 * @example sync
 * ```html
 * <ry-relative-time date="2026-05-30T12:00:00Z" sync></ry-relative-time>
 * <!-- Updates the visible text every 60s while mounted. -->
 * ```
 *
 * @example fr-locale
 * ```html
 * <ry-relative-time date="2026-05-30T11:55:00Z" lang="fr"></ry-relative-time>
 * <!-- → "il y a 5 minutes" -->
 * ```
 *
 * @tutorial past-date-renders-ago
 * ```js
 * // 5 minutes in the past renders an "ago" phrase.
 * const past = new Date(Date.now() - 5 * 60 * 1000).toISOString();
 * const el = mount(`<ry-relative-time date="${past}"></ry-relative-time>`);
 * const time = el.querySelector('[data-ry-target="time"]');
 * await expectEqual(/ago|minute/i.test(time.textContent), true, 'past date includes ago/minute');
 * ```
 *
 * @tutorial future-date-renders-in
 * ```js
 * // 2 hours in the future renders an "in ..." phrase.
 * const future = new Date(Date.now() + 2 * 60 * 60 * 1000).toISOString();
 * const el = mount(`<ry-relative-time date="${future}"></ry-relative-time>`);
 * const time = el.querySelector('[data-ry-target="time"]');
 * await expectEqual(/in|hour/i.test(time.textContent), true, 'future date includes in/hour');
 * ```
 *
 * @tutorial format-short
 * ```js
 * // Short style produces a different string than long, but both are non-empty.
 * const past = new Date(Date.now() - 5 * 60 * 1000).toISOString();
 * const el = mount(`<ry-relative-time date="${past}" format="short"></ry-relative-time>`);
 * const time = el.querySelector('[data-ry-target="time"]');
 * await expectEqual(time.textContent.length > 0, true, 'short format renders something');
 * ```
 *
 * @tutorial datetime-attribute-is-iso
 * ```js
 * // The inner <time> element gets the canonical ISO string in datetime=,
 * // regardless of how the date attribute was supplied.
 * const iso = '2026-05-30T12:00:00.000Z';
 * const el = mount(`<ry-relative-time date="${iso}"></ry-relative-time>`);
 * const time = el.querySelector('[data-ry-target="time"]');
 * await expectAttr(time, 'datetime', iso);
 * ```
 *
 * @tutorial sync-triggers-interval
 * ```js
 * // With sync=true, the component schedules a recurring update. We can prove
 * // the interval exists by observing the active timer count via fake timers.
 * vi.useFakeTimers();
 * const past = new Date(Date.now() - 5 * 60 * 1000).toISOString();
 * const el = mount(`<ry-relative-time date="${past}" sync></ry-relative-time>`);
 * await expectEqual(vi.getTimerCount() > 0, true, 'sync registered a timer');
 * vi.useRealTimers();
 * ```
 *
 * @tutorial disconnect-clears-interval
 * ```js
 * // Removing the element clears the registered timer — no leak.
 * vi.useFakeTimers();
 * const past = new Date(Date.now() - 5 * 60 * 1000).toISOString();
 * const el = mount(`<ry-relative-time date="${past}" sync></ry-relative-time>`);
 * el.remove();
 * await expectEqual(vi.getTimerCount(), 0, 'disconnect cleared the timer');
 * vi.useRealTimers();
 * ```
 *
 * @see ry-stat
 */

import { RyElement } from '../core/ry-element.js';

const UNITS: Array<{ max: number; value: number; unit: Intl.RelativeTimeFormatUnit }> = [
  { max: 2_760_000, value: 60_000, unit: 'minute' },           // max 46 minutes
  { max: 72_000_000, value: 3_600_000, unit: 'hour' },         // max 20 hours
  { max: 518_400_000, value: 86_400_000, unit: 'day' },        // max 6 days
  { max: 2_419_200_000, value: 604_800_000, unit: 'week' },    // max 28 days
  { max: 28_512_000_000, value: 2_592_000_000, unit: 'month' },// max 11 months
  { max: Infinity, value: 31_536_000_000, unit: 'year' },
];

const SYNC_INTERVAL_MS = 60_000;

export class RyRelativeTime extends RyElement {
  static observedAttributes = ['date', 'format', 'numeric', 'lang', 'sync'] as const;

  #intervalId: number | null = null;

  setup(): void {
    this.#render();
    this.#updateSyncInterval();
  }

  teardown(): void {
    this.#clearInterval();
  }

  attributeChangedCallback(): void {
    // Only react after first setup() has run (the initial attribute-set
    // happens before connectedCallback).
    if (!this.hasAttribute('data-ry-initialized')) return;
    this.#render();
    this.#updateSyncInterval();
  }

  get date(): string {
    return this.getAttribute('date') ?? '';
  }

  set date(value: string) {
    if (value == null) this.removeAttribute('date');
    else this.setAttribute('date', value);
  }

  get format(): 'long' | 'short' | 'narrow' {
    const v = this.getAttribute('format');
    return v === 'short' || v === 'narrow' ? v : 'long';
  }

  set format(value: 'long' | 'short' | 'narrow') {
    this.setAttribute('format', value);
  }

  get numeric(): 'always' | 'auto' {
    return this.getAttribute('numeric') === 'always' ? 'always' : 'auto';
  }

  set numeric(value: 'always' | 'auto') {
    this.setAttribute('numeric', value);
  }

  get sync(): boolean {
    return this.hasAttribute('sync');
  }

  set sync(value: boolean) {
    if (value) this.setAttribute('sync', '');
    else this.removeAttribute('sync');
  }

  #render(): void {
    const raw = this.getAttribute('date');
    const then = raw ? new Date(raw) : new Date();
    const now = new Date();

    if (isNaN(then.getTime())) {
      this.innerHTML = '';
      return;
    }

    const diff = then.getTime() - now.getTime();
    const chosen = UNITS.find(u => Math.abs(diff) < u.max) ?? UNITS[UNITS.length - 1]!;
    const { unit, value } = chosen;
    const iso = then.toISOString();

    const lang = this.getAttribute('lang') || (typeof document !== 'undefined' ? document.documentElement.lang : '') || undefined;

    let phrase = '';
    try {
      const formatter = new Intl.RelativeTimeFormat(lang, {
        numeric: this.numeric,
        style: this.format,
      });
      phrase = formatter.format(Math.round(diff / value), unit);
    } catch {
      phrase = '';
    }

    this.innerHTML = `<time data-ry-target="time" datetime="${iso}">${phrase}</time>`;
  }

  #updateSyncInterval(): void {
    if (this.sync) {
      if (this.#intervalId != null) return;
      this.#intervalId = (globalThis.setInterval(() => this.#render(), SYNC_INTERVAL_MS) as unknown) as number;
    } else {
      this.#clearInterval();
    }
  }

  #clearInterval(): void {
    if (this.#intervalId != null) {
      globalThis.clearInterval(this.#intervalId);
      this.#intervalId = null;
    }
  }
}

customElements.define('ry-relative-time', RyRelativeTime);
